import { useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import { Building2, Users, MessageCircle, Ghost, ExternalLink, Crown } from 'lucide-react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import MainLayout from '@/components/Layout/MainLayout'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table'
import { Input } from '@/components/ui/Input'
import { godModeAccounts } from '@/routeHelpers'
import type { CurrentUser } from '@/types/shared'
import type { GodModeAccount } from '@/types/godmode'

interface AccountsProps {
  current_user: CurrentUser
  accounts: GodModeAccount[]
  god_mode: boolean
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const columns: ColumnDef<GodModeAccount>[] = [
  {
    accessorKey: 'name',
    header: 'Account',
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <div className="size-8 rounded-lg overflow-hidden flex items-center justify-center bg-primary/20">
          {row.original.logo_url ? (
            <img
              src={row.original.logo_url}
              alt={row.original.name}
              className="size-full object-cover"
            />
          ) : (
            <Ghost className="size-4 text-primary" />
          )}
        </div>
        <div>
          <div className="font-medium">{row.original.name}</div>
          <div className="text-xs text-muted-foreground">{row.original.slug}</div>
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'users_count',
    header: 'Users',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Users className="size-4 text-muted-foreground" />
        <span>{row.original.users_count}</span>
      </div>
    ),
  },
  {
    accessorKey: 'conversations_count',
    header: 'Conversations',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <MessageCircle className="size-4 text-muted-foreground" />
        <span>{row.original.conversations_count}</span>
      </div>
    ),
  },
  {
    accessorKey: 'plan_name',
    header: 'Plan',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Crown className="size-4 text-amber-500" />
        <span>{row.original.plan_name}</span>
      </div>
    ),
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => formatDate(row.original.created_at),
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <Link
          href={godModeAccounts.show.path({ id: row.original.slug })}
          className="text-sm text-primary hover:underline cursor-pointer"
        >
          Manage
        </Link>
        <Link
          href={`/${row.original.slug}/inbox`}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <ExternalLink className="size-3" />
        </Link>
      </div>
    ),
  },
]

export default function Accounts({ current_user, accounts, god_mode }: AccountsProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')

  const table = useReactTable({
    data: accounts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    state: {
      sorting,
      globalFilter,
    },
  })

  return (
    <>
      <Head title="Accounts - God Mode" />
      <MainLayout
        user={current_user}
        breadcrumbs={[
          { label: 'God Mode' },
          { label: 'Accounts' },
        ]}
        godMode={god_mode}
      >
        <div className="flex-1 overflow-auto p-6">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="size-6 text-amber-500" />
              <h1 className="text-2xl font-semibold">Accounts</h1>
            </div>
            <p className="text-muted-foreground">
              View and manage all accounts across the platform.
            </p>
          </div>

          <div className="mb-4">
            <Input
              placeholder="Search accounts..."
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="max-w-sm"
            />
          </div>

          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No accounts found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 text-sm text-muted-foreground">
            Total: {accounts.length} accounts
          </div>
        </div>
      </MainLayout>
    </>
  )
}
